/*!
 * Adapted from "The Furrow - ASCIIxGPT - Concept 5" by The Furrow
 * (https://thefurrow.tv/project/asciixgpt). Changes: ported to a WebGL2 shader,
 * with a different pattern set, glyphs, colors, settings, and a carve around content.
 * This file is licensed under CC BY-NC-SA 4.0:
 * https://creativecommons.org/licenses/by-nc-sa/4.0/
 */

// The field's glyph atlas: one sprite per charset index, then the twinkle stars, drawn in
// `FONT_FAMILY` on a 2D canvas. Sprites are white; the shader only reads their alpha and picks
// the color itself.

import { TWINKLE_GLYPHS } from "./field-shaders.js";

// The field's glyphs look the same on every system: they come from a subset of DejaVu Sans Mono
// that the site ships (_fonts.scss; the font subsetting config lists the glyphs). DejaVu Sans
// Mono is Menlo's source, with the same outlines and metrics for these glyphs. Menlo only
// exists on Apple systems, so it's just the fallback.
export const FONT_FAMILY = '"DejaVu Sans Mono", Menlo, monospace';
export const FONT_SIZE = 12;
// Glyph tiers, from the heaviest circle to the lightest dot. The pattern gives each cell one.
// One entry per glyph, so splitting a string can never cut one in half.
export const GLYPHS: readonly string[] = ["⊚", "⊙", "◎", "◯", "◌", "•", "∙", "◦", "∘", "·"];

// How many times each glyph repeats in a run of the charset.
export const TIER_REPEAT = 2;

// The charset: each glyph `TIER_REPEAT` times, then the whole run three times
// (⊚⊚⊙⊙◎◎... ×3). The pattern maps a cell to a charset index, so more glyphs give finer steps
// over the same distance. The shader works out a glyph's tier from its index the same way.
function buildCharset(glyphs: readonly string[]): string[] {
  const run = glyphs.flatMap((g) => Array<string>(TIER_REPEAT).fill(g));
  return [...run, ...run, ...run];
}

export const CHARSET = buildCharset(GLYPHS);

/** A field cell's size and its sprite's margin, in device pixels. */
interface GlyphCell {
  width: number;
  height: number;
  pad: number;
}

/** Draws the atlas at `dpr`: the charset in its first row, the stars in its second. */
export function drawAtlas(
  atlas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  dpr: number,
): GlyphCell {
  // Until the glyph font loads, a glyph can come from a wider fallback font, so the cell fits
  // the widest one. Whole device pixels map each cell 1:1 onto its sprite.
  const font = `${FONT_SIZE * dpr}px ${FONT_FAMILY}`;
  ctx.font = font;
  const width = Math.ceil(
    Math.max(...[...CHARSET, ...TWINKLE_GLYPHS].map((g) => ctx.measureText(g).width)),
  );
  const height = Math.round(FONT_SIZE * dpr);
  // Each sprite pads its cell, so a glyph that a wave grows doesn't get cut off at the cell
  // edge. Ink overhangs the advance width by about 2% of the font size; 3% covers it.
  const pad = Math.ceil(FONT_SIZE * dpr * 0.03);
  const spriteW = width + 2 * pad;
  const spriteH = height + 2 * pad;
  // Resizing the atlas resets its context state.
  atlas.width = spriteW * Math.max(CHARSET.length, TWINKLE_GLYPHS.length);
  atlas.height = spriteH * 2;
  ctx.font = font;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#fff";
  // The charset repeats each glyph six times. Drawing text is the slow part of the page's
  // startup, so each glyph is drawn once and its sprite is copied to the other slots.
  const drawnAt = new Map<string, number>();
  CHARSET.forEach((glyph, g) => {
    const first = drawnAt.get(glyph);
    if (first === undefined) {
      drawnAt.set(glyph, g);
      ctx.fillText(glyph, (g + 0.5) * spriteW, spriteH / 2);
    } else {
      ctx.drawImage(atlas, first * spriteW, 0, spriteW, spriteH, g * spriteW, 0, spriteW, spriteH);
    }
  });
  TWINKLE_GLYPHS.forEach((glyph, s) => {
    ctx.fillText(glyph, (s + 0.5) * spriteW, spriteH * 1.5);
  });
  return { width, height, pad };
}
